import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import ApplicationsFilter, {
  ApplicationFilterState,
} from "../../components/ApplicationsFilter/ApplicationsFilter";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import Paginator, {
  PaginatorForRDT,
} from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import {
  VALID_PAYMENT_RESUBMISSIONS_STATUSES_AND_NEW_STATUS_MAP,
  VALID_RESUBMISSION_STATUSES_AND_NEW_STATUS_MAP,
} from "../../constants/Constants";
import { isUserOrArchitect } from "../../hoc/isUserOrArchitect";
import { getApplicationTypeColumnLabel } from "../../config/phaseConstants";
import {
  TApplication,
  useApplicationCounts,
  useApplications,
  useDeleteApplication,
  useNameOfServiceField,
} from "../../hooks/applications.hooks";
import { useDebounce, useToggle } from "../../hooks/common";
import { useApplicationAccessBlock } from "../../hooks/application-access.hooks";
import { formatDate } from "../../utils/date";
import SelectServiceTypeDialog from "./SelectServiceTypeDialog";
import { DeleteButton, ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";

export const Applications = isUserOrArchitect(() => {
  const navigate = useNavigate();
  const [filterOpen, toggleFilter] = useToggle();
  const [addApplication, toggleAddApplication] = useToggle();
  /**
   * An applicant the Admin has barred cannot start a new application. The
   * backend refuses the request as well; the button is switched off here so
   * the applicant learns why before filling the form.
   */
  const { isBlocked: accessBlock, notice: accessBlockNotice } =
    useApplicationAccessBlock();

  /**
   * Gunthewari is the only service, so New Application opens the form with it
   * straight away instead of asking for the Name of Service first. It is
   * found by name, so the option's id may differ between databases. When it
   * is missing or switched off, the old Name of Service dialog opens instead.
   */
  const { data: nameOfService } = useNameOfServiceField();
  const gunthewariService = nameOfService?.options?.find(
    (service) => service.active && /gunthewari/i.test(service.name)
  );
  const openNewApplication = () => {
    if (!gunthewariService) return toggleAddApplication();
    navigate("/add-new-application", {
      state: { name_of_service: gunthewariService.id },
    });
  };
  const [filterState, setFilterState] = useState<ApplicationFilterState>({
    connector: "and",
    conditions: [],
  });
  const { mutate: deleteApplication, isPending: deletingApplication } =
    useDeleteApplication();
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"table" | "card">("table");
  const [page, setPage] = useState(1);
  const search = useDebounce(searchQuery);
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "submittedAt",
    sortOrder: -1,
  });
  const { isLoading: isLoading1, data: counts } = useApplicationCounts({
    search,
    filters: filterState,
  });
  const { isLoading: isLoading2, data } = useApplications({
    page,
    search,
    limit: 10,
    sort: sortKey,
    sortVal: sortOrder,
    filters: filterState,
  });

  const columns = useMemo(
    (): TableColumn<TApplication & { serviceName?: string }>[] => [
      {
        name: "Application ID",
        width: "180px",
        sortable: true,
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
      },
      {
        name: "Plot Owner's Name",
        sortable: true,
        sortField: "plot_owner_s_name",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 2,
      },
      {
        sortField: "name_of_service",
        sortable: true,
        name: "Name of Service",
        width: "230px",
        cell: (d) => (
          <span className="text-center">{d.serviceName || "-"}</span>
        ),
        center: true,
      },
      {
        name: "Application Type",
        selector: (d) => getApplicationTypeColumnLabel(d),
        center: true,
        width: "180px",
      },
      {
        name: "Status",
        width: "225px",
        center: true,
        sortable: true,
        sortField: "status",
        cell: (d) => <span className="text-center">{d.status}</span>,
        grow: 2,
      },
      {
        name: "Date of Submission",
        width: "220px",
        center: true,
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
        grow: 2,
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        grow: 0.5,
        cell: (e) => (
          <div className="flex space-x-2">
            <ViewButton
              onClick={() => {
                if (e.status === "Draft") {
                  navigate("/edit-application", { state: { id: e.id } });
                } else if (
                  VALID_RESUBMISSION_STATUSES_AND_NEW_STATUS_MAP[e.status]
                ) {
                  navigate("/resubmit-application", {
                    state: { id: e.id },
                  });
                } else if (
                  VALID_PAYMENT_RESUBMISSIONS_STATUSES_AND_NEW_STATUS_MAP[
                  e.status
                  ]
                ) {
                  // Referred from @Anusthan's Changes
                  navigate("/pay-now", {
                    state: {
                      user: {
                        ...e,
                        id: e.id,
                        application_id: e.applicationId,
                      },
                      paymentStatus: e.status,
                    },
                  });
                } else navigate("/view-application", { state: { id: e.id } });
              }}
            />
            <DeleteButton
              onClick={() => deleteApplication(e.id)}
              className={
                e.status === "Draft" ? "" : "pointer-events-none opacity-0"
              }
              disabled={!(e?.status === 'Draft')}
            />
          </div>
        ),
      },
    ],
    [navigate, deleteApplication, deletingApplication]
  );

  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between items-center flex-wrap lg:flex-nowrap gap-4 mb-4">
        <h4 className="text-2xl font-bold">All Applications</h4>
        <div className="flex gap-4 grow lg:grow-0 flex-wrap md:flex-nowrap">
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
          >
            {view === "table" ? (
              <i className="fa fa-th text-primary m-0" />
            ) : (
              <i className="fa fa-list-ul text-primary m-0" />
            )}
          </button>
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
            onClick={toggleFilter}
          >
            <i className="fa fa-filter text-primary m-0" />
          </button>
          <div className="flex rounded-xl border grow lg:grow-0 border-[#968f8f] bg-white items-stretch overflow-hidden">
            <input
              type="text"
              placeholder="Search..."
              className="bg-none px-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="p-3 shadow-md flex justify-center items-center">
              <i className="fa fa-search text-primary m-0" />
            </div>
          </div>
          {/*
            Before:
            <button
              className="grow lg:grow-0 py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg"
              onClick={toggleAddApplication}
            >
          */}
          <button
            className="grow lg:grow-0 py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            /** Before: onClick={toggleAddApplication} - always asked for the Name of Service. */
            onClick={openNewApplication}
            disabled={accessBlock}
            title={accessBlockNotice}
          >
            New Application
          </button>
        </div>
      </div>
      {accessBlock && (
        <p className="text-sm font-semibold text-red-700 mb-2">
          {accessBlockNotice}
        </p>
      )}
      {view === "table" ? (
        <BoxComponent>
          <DataTable
            pagination
            responsive
            sortServer
            paginationServer
            className="mt-4 rounded-0"
            columns={columns}
            data={data?.data || []}
            sortIcon={<SortIcon />}
            customStyles={tableCustomStyles}
            onChangePage={(p) => setPage(p)}
            paginationTotalRows={counts?.data || 0}
            progressPending={isLoading1 || isLoading2}
            onSort={(col, dir) => {
              if (!col?.sortField) return;
              setSort({
                sortKey: col.sortField || "",
                sortOrder: dir === "asc" ? 1 : -1,
              });
            }}
            paginationComponent={PaginatorForRDT}
          />
        </BoxComponent>
      ) : (
        <>
          <BoxComponent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(data?.data || []).map((e) => (
                <div
                  className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300"
                  key={e.id}
                >
                  <div className="flex justify-between items-start space-x-4 mb-4">
                    <div
                      className={
                        "text-center bg-yellow-500 text-grey-600 inline-block py-1 px-4 rounded-full text-base text-[12px] font-semibold"
                      }
                    >
                      {e.status}
                    </div>
                    <div className="flex justify-center space-x-1">
                      <ViewButton
                        onClick={() => {
                          if (e.status === "Draft") {
                            navigate("/edit-application", {
                              state: { id: e.id },
                            });
                          } else if (
                            VALID_RESUBMISSION_STATUSES_AND_NEW_STATUS_MAP[
                            e.status
                            ]
                          ) {
                            navigate("/resubmit-application", {
                              state: { id: e.id },
                            });
                          } else if (
                            VALID_PAYMENT_RESUBMISSIONS_STATUSES_AND_NEW_STATUS_MAP[
                            e.status
                            ]
                          ) {
                            // Referred from @Anusthan's Changes
                            navigate("/pay-now", {
                              state: {
                                user: {
                                  ...e,
                                  id: e.id,
                                  application_id: e.applicationId,
                                },
                                paymentStatus: e.status,
                              },
                            });
                          } else
                            navigate("/view-application", {
                              state: { id: e.id },
                            });
                        }}
                      />
                      <DeleteButton
                        onClick={() => deleteApplication(e.id)}
                        className={
                          e.status === "Draft"
                            ? ""
                            : "pointer-events-none opacity-0"
                        }
                        disabled={!(e?.status === 'Draft')}
                      />
                    </div>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">Application ID</p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{e.applicationId}</p>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">
                      Plot Owner's Name
                    </p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">
                      {e.plot_owner_s_name || "-"}
                    </p>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">Name of Service</p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{e.serviceName || "-"}</p>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">
                      Last Published Date
                    </p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{formatDate(e.updatedAt)}</p>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">
                      Date of Submission
                    </p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">
                      {formatDate(e.submittedAt || "x")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Paginator
              limit={10}
              page={page}
              setPage={setPage}
              count={counts?.data || 0}
            />
          </BoxComponent>
        </>
      )}
      <Dialog
        onClose={toggleFilter}
        open={filterOpen}
        className="flex flex-col sm:w-[600px] lg:w-[800px]"
      >
        <ApplicationsFilter
          onClose={toggleFilter}
          defaultFilter={filterState}
          onChange={setFilterState}
        />
      </Dialog>
      {!!addApplication && (
        <SelectServiceTypeDialog onClose={toggleAddApplication} />
      )}
    </div>
  );
});
